import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { PropsWithChildren } from "react";
import { useStore } from "@/stores/useStore";
import { Separator } from "../Base/Separator";
import { AppVersion } from "@/components/Common/AppVersion";
import { useRoles } from "@/hooks/useRoles";
import { useAuth } from "@/hooks/store/useAuth";
import {
  EmployeeRole,
  FEATURE_FLAGS,
  IconNames,
  TerminologyOptions,
} from "@/types/v2";
import { Icon } from "@/components/Common/Icon";
import { ClassNameValue } from "tailwind-merge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";
import { Button } from "@/components/Base/Button";
import { ActiveShopSelect } from "@/components/Page/ActiveShopSelect";
import { FeatureFlagged } from "@/components/Common/FeatureFlagged";
import { LoggedInUser } from "@/components/Common/LoggedInUser";
import { useCustomerUpdatesConfig } from "@/hooks/shop/useCustomerUpdatesConfig";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";
import { SoundAlertsSidebarItem } from "@/components/SoundPreferences/SoundAlertsSidebarItem";

export interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const SideBarItem = ({ children }: PropsWithChildren) => {
  return <div className="mb-2.5 flex flex-row items-center">{children}</div>;
};

export const Sidebar = ({ open, onClose }: SidebarProps) => {
  const { isSuperAdmin, isAdmin, isOffice } = useRoles();
  const { setShopRole, shopRole, authState } = useStore();
  const { terminolize } = useTerminologyReplacer();
  const currentUrl = window.location.href;
  const activeShopId = useStore((s) => s.shopId);
  const { logout } = useAuth();
  const allowedShops = useStore((s) => s.allowedShops);
  const customerUpdateConfig = useCustomerUpdatesConfig();
  const handleLogout = () => logout();

  const handleChangeShop = (shopId: string) => {
    if (activeShopId) {
      const newUrl = currentUrl.replace(activeShopId, shopId);
      return (window.location.href = newUrl);
    }
    window.location.href = `/${shopId}`;
  };

  const handleShopRoleChange = (role: EmployeeRole) => {
    setShopRole(role);
  };

  const iconClassName: ClassNameValue = "mr-2.5 mt-[3px] size-5 text-slate-400";

  return (
    <aside
      className={cn(
        "absolute inset-0 z-10 h-full w-[100vw] text-sm text-slate-100",
        open ? "pointer-events-auto" : "pointer-events-none"
      )}
    >
      {/* Backdrop: covers the whole screen, fades in (no position animation) */}
      <div
        className={cn(
          "absolute inset-0 bg-black/80 transition-opacity duration-100",
          open ? "opacity-100" : "opacity-0"
        )}
        onClick={onClose}
      />
      {/* Nav menu: sits above the backdrop (higher z-index), slides in */}
      <div
        className={cn(
          "absolute left-0 top-0 z-10 flex h-full w-64 flex-col overflow-y-auto bg-slate-900 p-2 shadow-xl transition-transform duration-100",
          open ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {isSuperAdmin && (
          <SideBarItem>
            <div className={"flex w-full flex-col items-start"}>
              <p className="text-xs text-primary-foreground">Shop Role</p>
              <div>
                <Select
                  onValueChange={(value) =>
                    handleShopRoleChange(value as EmployeeRole)
                  }
                  value={shopRole}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Shop Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="office">Office</SelectItem>
                    <SelectItem value="technician">Technician</SelectItem>
                    <SelectItem value="viewOnly">View Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </SideBarItem>
        )}
        <SideBarItem>
          {allowedShops?.length > 1 && (
            <div className={"mr-4 mt-5 w-full"}>
              <p className="text-center text-xs text-primary-foreground">
                Active Shop:
              </p>
              <ActiveShopSelect
                shops={allowedShops}
                activeShopId={activeShopId}
                onChange={handleChangeShop}
              />
            </div>
          )}
        </SideBarItem>

        {authState !== "unverified" && (
          <>
            <SideBarItem>
              <Icon name={IconNames.dashboard} className={iconClassName} />
              <Link
                to={activeShopId ? `/${activeShopId}` : "/"}
                onClick={onClose}
              >
                Dashboard
              </Link>
            </SideBarItem>
            <SideBarItem>
              <Icon name={IconNames.activityFeed} className={iconClassName} />
              <Link to={`/${activeShopId}/activityFeed`} onClick={onClose}>
                Activity Feed
              </Link>
            </SideBarItem>
            <SideBarItem>
              <Icon name={IconNames.messages} className={iconClassName} />
              <Link to={`/${activeShopId}/messageBoard`} onClick={onClose}>
                Message Board
              </Link>
            </SideBarItem>

            <SideBarItem>
              <Icon name={IconNames.container} className={iconClassName} />
              <Link to={`/${activeShopId}/containers`} onClick={onClose}>
                Containers
              </Link>
            </SideBarItem>

            <SideBarItem>
              <Icon name={IconNames.card} className={iconClassName} />
              <Link to={`/${activeShopId}/cards`} onClick={onClose}>
                {terminolize(
                  "Repair Orders",
                  TerminologyOptions.repairOrder,
                  "plural"
                )}
              </Link>
            </SideBarItem>

            {(isAdmin || isOffice) && (
              <FeatureFlagged feature={FEATURE_FLAGS.isCustomerUpdatesEnabled}>
                {customerUpdateConfig.enabled && (
                  <SideBarItem>
                    <Icon
                      name={IconNames.customerUpdate}
                      className={iconClassName}
                    />
                    <Link
                      to={`/${activeShopId}/customer-updates`}
                      onClick={onClose}
                    >
                      Customer Updates
                    </Link>
                  </SideBarItem>
                )}
              </FeatureFlagged>
            )}
            {(isAdmin || isOffice) && (
              <FeatureFlagged feature={FEATURE_FLAGS.isMetricsEnabled}>
                <SideBarItem>
                  <Icon name={IconNames.metrics} className={iconClassName} />
                  <Link to={`/${activeShopId}/metrics`} onClick={onClose}>
                    Metrics
                  </Link>
                </SideBarItem>
              </FeatureFlagged>
            )}
            {(isAdmin || isOffice) && (
              <FeatureFlagged feature={FEATURE_FLAGS.isContainerMetricsEnabled}>
                <SideBarItem>
                  <Icon name={IconNames.container} className={iconClassName} />
                  <Link
                    to={`/${activeShopId}/containerMetrics`}
                    onClick={onClose}
                  >
                    Container Metrics
                  </Link>
                </SideBarItem>
              </FeatureFlagged>
            )}
          </>
        )}

        {(isAdmin || isOffice) && (
          <>
            <Separator className="my-2" />
            {activeShopId && (
              <>
                <RoleEnabledLink enabled={isAdmin || isSuperAdmin}>
                  <SideBarItem>
                    <Icon name={IconNames.shop} className={iconClassName} />
                    <Link
                      to={`/${activeShopId}/settings/shop`}
                      onClick={onClose}
                    >
                      Shop Settings
                    </Link>
                  </SideBarItem>
                </RoleEnabledLink>
                <SideBarItem>
                  <Icon name={IconNames.layouts} className={iconClassName} />
                  <Link to={`/${activeShopId}/layoutBuilder`} onClick={onClose}>
                    Layout Builder
                  </Link>
                </SideBarItem>
                <SideBarItem>
                  <Icon name={IconNames.automation} className={iconClassName} />
                  <Link to={`/${activeShopId}/automations`} onClick={onClose}>
                    Automations
                  </Link>
                </SideBarItem>
                <SideBarItem>
                  <Icon name={IconNames.card} className={iconClassName} />
                  <Link to={`/${activeShopId}/cardConfig`} onClick={onClose}>
                    Card Configuration
                  </Link>
                </SideBarItem>
                <SideBarItem>
                  <Icon
                    name={IconNames.integration}
                    className={iconClassName}
                  />
                  <Link to={`/${activeShopId}/integrations`} onClick={onClose}>
                    Integrations
                  </Link>
                </SideBarItem>
              </>
            )}
          </>
        )}
        {isAdmin && (
          <>
            <SideBarItem>
              <Icon name={IconNames.employees} className={iconClassName} />
              <Link to={`/${activeShopId}/employees`} onClick={onClose}>
                Employees
              </Link>
            </SideBarItem>
          </>
        )}
        {isSuperAdmin && (
          <SideBarItem>
            <Icon name={IconNames.featureFlags} className={iconClassName} />
            <Link to={`${activeShopId}/features`} onClick={onClose}>
              Shop Feature Flags
            </Link>
          </SideBarItem>
        )}

        {isSuperAdmin && (
          <>
            <Separator className="my-2" />
            <SideBarItem>
              <Icon
                name={IconNames.globalFeatureFlags}
                className={iconClassName}
              />
              <Link to="/features/global" onClick={onClose}>
                Global Feature Flags
              </Link>
            </SideBarItem>
            <SideBarItem>
              <Icon name={IconNames.settings} className={iconClassName} />
              <Link to="/config/app" onClick={onClose}>
                Admin Settings
              </Link>
            </SideBarItem>
            <SideBarItem>
              <Icon name={IconNames.shop} className={iconClassName} />
              <Link to="/shops" onClick={onClose}>
                Shops
              </Link>
            </SideBarItem>
            <SideBarItem>
              <Icon name={IconNames.users} className={iconClassName} />
              <Link to="/users" onClick={onClose}>
                Users
              </Link>
            </SideBarItem>
            <SideBarItem>
              <Icon name={IconNames.clipboardCheck} className={iconClassName} />
              <Link to="/shop-config-audit" onClick={onClose}>
                Shop Config Audit
              </Link>
            </SideBarItem>
          </>
        )}

        <div className="flex-grow" />
        <SideBarItem>
          <SoundAlertsSidebarItem
            onOpen={onClose}
            iconClassName={String(iconClassName)}
          />
        </SideBarItem>
        <SideBarItem>
          <Icon name={IconNames.support} className={iconClassName} />
          <a
            href="https://www.shopherodashboard.com/support-request"
            target="_blank"
          >
            Support
          </a>
        </SideBarItem>
        <SideBarItem>
          <Button
            variant={"ghost-alt"}
            onClick={handleLogout}
            className="flex flex-row items-center gap-0 pl-0"
          >
            <Icon name={IconNames.logout} className={iconClassName} />
            Logout
          </Button>
        </SideBarItem>
        <LoggedInUser />

        <AppVersion />
      </div>
    </aside>
  );
};

const RoleEnabledLink = ({
  enabled,
  children,
}: {
  enabled: boolean;
  children: React.ReactNode;
}) => {
  if (enabled) {
    return <>{children}</>;
  }
  return <></>;
};
